import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Calendar, GeoAlt, InfoCircle, SendFill } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { updateJobSetup, selectCalendarLoading, clearJobSetupError, selectSectionTemplates } from "../slice";
import { navigateToAppointment, formatHouseType } from "../utils/mapUtils";
import CustomToast from "../../../components/common/CustomToast";
import "./JobSetupModal.css";

const JobSetupModal = ({ show, onHide, appointment }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectCalendarLoading);
  const sectionTemplates = useSelector(selectSectionTemplates);

  // Form state
  const [formData, setFormData] = useState({
    template: null,
    quote: "",
    note: ""
  });

  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  // Determine if we're in edit mode based on existing job data
  const isEditMode = appointment?.job?.note || appointment?.job?.quote;

  // Initialize form data when modal opens or appointment changes
  useEffect(() => {
    if (show && appointment) {
      // Clear any previous errors and toast messages
      dispatch(clearJobSetupError());
      setShowToast(false);
      setToastMessage("");

      // Pre-populate form if in edit mode
      const jobData = appointment.job || {};

      // Find the template object from sectionTemplates if template ID exists
      let selectedTemplate = null;
      const templateId = jobData.template?.id;
      if (templateId) {
        selectedTemplate = sectionTemplates.find(template => template.id === templateId) || null;
      } else {
        // If no template exists, use system default
        selectedTemplate = sectionTemplates.find(template => template.isSystemDefault) || null;
      }

      setFormData({
        template: selectedTemplate,
        quote: jobData.quote || "",
        note: jobData.note || ""
      });
      setFormErrors({});
    }
  }, [show, appointment, dispatch, sectionTemplates]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Quote validation (optional but must be valid number if provided)
    if (formData.quote && (isNaN(formData.quote) || parseFloat(formData.quote) < 0)) {
      errors.quote = "Please enter a valid quote amount";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Get job ID from appointment
      const jobId = appointment.job?.id;
      
      if (!jobId) {
        throw new Error("Job ID not found in appointment");
      }

      // Prepare job setup data according to API spec
      const jobSetupData = {
        note: formData.note || "",
        quote: formData.quote ? formData.quote : null,
        template_id: formData.template ? formData.template.id : null
      };

      // Both add and edit use the same PUT endpoint
      await dispatch(updateJobSetup({ jobId, jobSetupData })).unwrap();
      
      setToastMessage(isEditMode ? "Job Setup updated successfully." : "Job Setup created successfully.");
      setToastVariant("success");
      setShowToast(true);
      
      // Close modal after a short delay to show the toast
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} job setup:`, error);
      
      // Show error toast but keep modal open
      setToastMessage(`Failed to ${isEditMode ? 'update' : 'create'} Job Setup. Please try again.`);
      setToastVariant("error");
      setShowToast(true);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    onHide();
  };

  // Extract appointment data for display
  const getAppointmentDisplayData = () => {
    if (!appointment) return null;

    // Get datetime from appointment start_time and end_time
    const startTime = moment((appointment.start_time || appointment.start).substring(0, 19));
    const endTime = moment((appointment.end_time || appointment.end).substring(0, 19));
    const dateTimeDisplay = `${startTime.format("MMM D, h:mm A")} - ${endTime.format("h:mm A")}`;

    // Get address data from appointment.job.report.address or fallback
    let addressDisplay = "";
    const reportAddress = appointment.job?.report?.address;
    
    if (reportAddress && typeof reportAddress === 'object') {
      const addressParts = [
        reportAddress.address_line1,
        reportAddress.address_line2,
        reportAddress.city,
        reportAddress.state,
        reportAddress.zip_code
      ].filter(Boolean);
      addressDisplay = addressParts.join(", ");
    } else if (typeof reportAddress === 'string') {
      addressDisplay = reportAddress;
    } else {
      // Fallback to job.address or direct appointment fields
      const jobAddress = appointment.job?.address;
      if (jobAddress && typeof jobAddress === 'object') {
        const addressParts = [
          jobAddress.address_line1,
          jobAddress.address_line2,
          jobAddress.city,
          jobAddress.state,
          jobAddress.zip_code
        ].filter(Boolean);
        addressDisplay = addressParts.join(", ");
      }
    }

    // Get property details from appointment.job.report or fallback
    const report = appointment.job?.report || {};
    const propertyDetails = {
      yearBuilt: report.year_build || appointment.year_build || "",
      squareFeet: report.floor_area || appointment.floor_area || "",
      numberOfStories: report.stories || appointment.stories || "",
      houseType: report.house_type || appointment.house_type || "",
      parcelNumber: report.apn || appointment.apn || "",
      note: report.info || appointment.info || ""
    };

    return {
      dateTimeDisplay,
      addressDisplay,
      propertyDetails
    };
  };

  const displayData = getAppointmentDisplayData();

  if (!displayData) {
    return null;
  }

  // Check if any property details exist
  const hasAnyPropertyDetails = () => {
    return displayData.propertyDetails.yearBuilt ||
           displayData.propertyDetails.squareFeet ||
           displayData.propertyDetails.numberOfStories ||
           displayData.propertyDetails.houseType ||
           displayData.propertyDetails.parcelNumber ||
           displayData.propertyDetails.note;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="job-setup-modal"
      backdrop="static"
    >
      <div className="job-setup-form">
        {/* Header */}
        <div className="job-setup-header">
          <h5 className="job-setup-title">
            {isEditMode ? "Edit Job Setup" : "Add Job Setup"}
          </h5>
          <Button 
            variant="link" 
            className="job-setup-close" 
            onClick={handleClose}
          >
            <i className="bi bi-x" style={{ fontSize: "16px", opacity: 0.5 }}></i>
          </Button>
        </div>

        {/* Content */}
        <div className="job-setup-content">
          <Form onSubmit={handleSubmit}>
            {/* Job Information Section */}
            <div className="job-info-section">
              {/* Appointment Info */}
              <div className="appointment-info">
                <div className="datetime-display">
                  <Calendar className="datetime-icon" />
                  <span className="datetime-text">{displayData.dateTimeDisplay}</span>
                  {appointment.note && (
                    <OverlayTrigger
                      placement="bottom"
                      delay={{ show: 250, hide: 400 }}
                      overlay={
                        <Tooltip id="appointment-note-tooltip" className="custom-tooltip">
                          {appointment.note}
                        </Tooltip>
                      }
                    >
                      <span>
                        <InfoCircle size={16} className="info-icon" />
                      </span>
                    </OverlayTrigger>
                  )}
                </div>
              </div>

              {/* Site Information */}
              <div className="site-info">
                <div className="address-section">
                  <GeoAlt className="location-icon" />
                  <span className="address-text">{displayData.addressDisplay}</span>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="navigation-button"
                    onClick={() => navigateToAppointment(appointment)}
                  >
                    <SendFill size={16} />
                  </Button>
                </div>

                {/* Property Details */}
                {hasAnyPropertyDetails() && (
                  <div className="job-setup-property-details">
                    <div className="setup-property-grid">
                      {displayData.propertyDetails.yearBuilt && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">Year Built</span>
                          <span className="setup-property-value">{displayData.propertyDetails.yearBuilt}</span>
                        </div>
                      )}
                      {displayData.propertyDetails.squareFeet && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">Square Feet</span>
                          <span className="setup-property-value">{displayData.propertyDetails.squareFeet}</span>
                        </div>
                      )}
                      {displayData.propertyDetails.numberOfStories && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">Number of Stories</span>
                          <span className="setup-property-value">{displayData.propertyDetails.numberOfStories}</span>
                        </div>
                      )}
                      {displayData.propertyDetails.houseType && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">House Type</span>
                          <span className="setup-property-value">{formatHouseType(displayData.propertyDetails.houseType)}</span>
                        </div>
                      )}
                      {displayData.propertyDetails.parcelNumber && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">Parcel Number</span>
                          <span className="setup-property-value">{displayData.propertyDetails.parcelNumber}</span>
                        </div>
                      )}
                      {displayData.propertyDetails.note && (
                        <div className="setup-property-field">
                          <span className="setup-property-label">Note</span>
                          <span className="setup-property-value">{displayData.propertyDetails.note}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <hr className="section-divider" />

            {/* Section Template Setting */}
            <div className="section-container">
              <h6 className="job-setup-section-title">Section Template Setting</h6>
              <div className="section-fields">
                <Form.Group>
                  <Form.Label>Section Template</Form.Label>
                  <Form.Select
                    value={
                      formData.template
                        ? formData.template.id
                        : sectionTemplates.find(t => t.isSystemDefault)?.id || ""
                    }
                    onChange={(e) => {
                      const templateId = e.target.value;
                      const selectedTemplate = templateId ? sectionTemplates.find(t => t.id === templateId) : null;
                      handleInputChange("template", selectedTemplate);
                    }}
                    className="section-template-select"
                    disabled={appointment?.job?.report?.item_count > 0}
                  >
                    {sectionTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Form.Select>
                  {appointment?.job?.report?.item_count > 0 && (
                    <div className="template-disabled-notice">
                      <InfoCircle size={16} style={{ color: '#997404' }} />
                      <span style={{ color: '#997404', fontSize: '14px', marginLeft: '6px' }}>
                        Not editable after inspection starts or is completed.
                      </span>
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>

            {/* Quote Info Section */}
            <div className="section-container">
              <h6 className="job-setup-section-title">Quote Info</h6>
              <div className="section-fields">
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Quote</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter quote amount"
                        value={formData.quote}
                        onChange={(e) => handleInputChange("quote", e.target.value)}
                        isInvalid={!!formErrors.quote}
                        min="0"
                        step="0.01"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.quote}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Add note..."
                        value={formData.note}
                        onChange={(e) => handleInputChange("note", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          </Form>
        </div>

        {/* Footer */}
        <div className="job-setup-footer">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading.jobSetup}>
            {loading.jobSetup ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top-end"
        delay={4000}
        autohide={true}
      />
    </Modal>
  );
};

export default JobSetupModal;